import { useState, useEffect, useRef } from "react";
import { busApi } from "../services/api/busApi";

/**
 * Hook para rastreamento em tempo real de veículos
 * @param {Object} filters - Filtros para buscar veículos
 * @param {string} filters.routeId - ID de uma linha específica
 * @param {string} filters.routeIds - IDs de múltiplas linhas (separado por vírgula)
 * @param {string|number} filters.directionId - Sentido da viagem ("0"/"1" ou 0/1), quando suportado pelo backend
 * @param {string} filters.bounds - Bounding box (minLat,minLng,maxLat,maxLng)
 * @param {number} filters.limit - Limite de veículos
 * @param {number} intervalMs - Intervalo de atualização em milissegundos (padrão: 30 segundos)
 * @param {boolean} enabled - Se o rastreamento está ativo (padrão: true)
 * @returns {Object} { vehicles, loading, error, refetch }
 */
export const useVehicleTracking = (
  filters = {},
  intervalMs = 30000,
  enabled = true
) => {
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const intervalRef = useRef(null);
  const filtersStringified = JSON.stringify(filters);

  const fetchVehicles = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await busApi.getVehiclePositions(filters);
      const data = response?.data;
      const vehiclesData = Array.isArray(data) ? data : data?.vehicles || [];

      setVehicles(vehiclesData);
    } catch (err) {
      console.error("Erro ao buscar veículos:", err);
      setError(err);
      setVehicles([]); // Limpar veículos em caso de erro
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Se desabilitado, limpar intervalo e veículos
    if (!enabled) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      setVehicles([]);
      return;
    }

    // Buscar imediatamente ao habilitar
    fetchVehicles();

    // Configurar polling apenas se intervalo for maior que 0
    if (intervalMs > 0) {
      intervalRef.current = setInterval(fetchVehicles, intervalMs);
    }

    // Cleanup ao desmontar ou quando dependências mudarem
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, intervalMs, filtersStringified]);

  return {
    vehicles,
    loading,
    error,
    refetch: fetchVehicles,
  };
};

export default useVehicleTracking;
